import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

const PERM_KEY = "urgent_notifications_enabled";
const LAST_FETCH_KEY = "urgent_notifs_last_fetch";
const SEEN_KEY = "urgent_notifs_seen";

function getSeenIds() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}

function markSeen(ids) {
  try {
    const cur = getSeenIds();
    ids.forEach((id) => cur.add(id));
    localStorage.setItem(SEEN_KEY, JSON.stringify([...cur].slice(-200)));
  } catch { /* ignore */ }
}

/**
 * Polls /api/notifications/urgent every 5 min and shows browser notifications.
 * Strict scope: only urgent items (Bouclier expiring < 2h, national scam alerts).
 * Requires user to have explicitly enabled in Accessibility panel.
 */
export default function UrgentNotifications() {
  const [enabled, setEnabled] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const onChange = () => {
      try { setEnabled(localStorage.getItem(PERM_KEY) === "1"); }
      catch { /* ignore */ }
    };
    onChange();
    window.addEventListener("urgent-notifs-changed", onChange);
    return () => window.removeEventListener("urgent-notifs-changed", onChange);
  }, []);

  const showBrowserNotif = (n) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    try {
      const opts = {
        body: n.body,
        icon: "/icons/icon-192.png",
        badge: "/icons/icon-192.png",
        tag: n.id,
        data: { url: n.action_url || "/sos" },
      };
      const notif = new Notification(n.title, opts);
      notif.onclick = (e) => {
        e.preventDefault();
        window.focus();
        if (n.action_url) window.location.href = n.action_url;
      };
    } catch { /* ignore */ }
  };

  const fetchAndNotify = async () => {
    try {
      const res = await api.get("/notifications/urgent");
      const items = Array.isArray(res.data) ? res.data : [];
      const seen = getSeenIds();
      const fresh = items.filter((n) => !seen.has(n.id));
      fresh.forEach(showBrowserNotif);
      if (fresh.length > 0) markSeen(fresh.map((n) => n.id));
      try { localStorage.setItem(LAST_FETCH_KEY, new Date().toISOString()); }
      catch { /* ignore */ }
    } catch { /* silent: user might be logged out */ }
  };

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    // Initial fetch + 5 min interval
    fetchAndNotify();
    timerRef.current = setInterval(fetchAndNotify, 5 * 60 * 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [enabled]);

  return null;
}

/** Helper: request browser permission and toggle the setting */
export async function enableUrgentNotifications() {
  if (!("Notification" in window)) return { ok: false, reason: "unsupported" };
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };
  try { localStorage.setItem(PERM_KEY, "1"); } catch { /* ignore */ }
  window.dispatchEvent(new Event("urgent-notifs-changed"));
  return { ok: true };
}

export function disableUrgentNotifications() {
  try { localStorage.setItem(PERM_KEY, "0"); } catch { /* ignore */ }
  window.dispatchEvent(new Event("urgent-notifs-changed"));
}

export function isUrgentNotificationsEnabled() {
  try { return localStorage.getItem(PERM_KEY) === "1" && Notification.permission === "granted"; }
  catch { return false; }
}
